import { useCallback, useEffect, useRef, useState } from 'react';

interface UseLocalStorageSetReturn {
  values: Set<string>;
  addValue: (value: string) => void;
  removeValue: (value: string) => void;
  setValue: (newSet: Set<string> | string[]) => void;
  clearValues: () => void;
  hasValue: (value: string) => boolean;
}

const LOCAL_STORAGE_SET_SYNC_EVENT = 'local-storage-set-sync';

function readSetFromStorage(key: string, initialValues: string[]) {
  try {
    const item = localStorage.getItem(key);
    if (item) {
      const parsed = JSON.parse(item);
      return new Set<string>(Array.isArray(parsed) ? parsed : []);
    }
    return new Set<string>(initialValues);
  } catch (error) {
    console.error(`Error reading localStorage key "${key}":`, error);
    return new Set<string>(initialValues);
  }
}

function setsEqual(a: Set<string>, b: Set<string>) {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

export const useLocalStorageSet = (
  key: string,
  initialValues: string[] = []
): UseLocalStorageSetReturn => {
  const initialValuesRef = useRef(initialValues);
  const sourceIdRef = useRef<string>(crypto.randomUUID());

  const [values, setValues] = useState<Set<string>>(() =>
    readSetFromStorage(key, initialValuesRef.current)
  );

  useEffect(() => {
    const syncFromStorage = () => {
      const next = readSetFromStorage(key, initialValuesRef.current);
      setValues((prev) => (setsEqual(prev, next) ? prev : next));
    };

    const onCustomSync = (e: Event) => {
      const detail = (e as CustomEvent<{ key: string; sourceId?: string }>).detail;
      if (detail?.key !== key) return;

      if (detail?.sourceId && detail.sourceId === sourceIdRef.current) return;

      syncFromStorage();
    };

    window.addEventListener('storage', syncFromStorage);
    window.addEventListener(LOCAL_STORAGE_SET_SYNC_EVENT, onCustomSync);

    return () => {
      window.removeEventListener('storage', syncFromStorage);
      window.removeEventListener(LOCAL_STORAGE_SET_SYNC_EVENT, onCustomSync);
    };
  }, [key]);

  useEffect(() => {
    try {
      const arrayValues = Array.from(values);
      localStorage.setItem(key, JSON.stringify(arrayValues));

      window.dispatchEvent(
        new CustomEvent(LOCAL_STORAGE_SET_SYNC_EVENT, {
          detail: { key, sourceId: sourceIdRef.current },
        })
      );
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, values]);

  const addValue = useCallback((value: string) => {
    setValues((prevValues) => {
      const newSet = new Set(prevValues);
      newSet.add(value);
      return newSet;
    });
  }, []);

  const removeValue = useCallback((value: string) => {
    setValues((prevValues) => {
      const newSet = new Set(prevValues);
      newSet.delete(value);
      return newSet;
    });
  }, []);

  const setValue = useCallback((newSet: Set<string> | string[]) => {
    setValues(new Set(newSet));
  }, []);

  const clearValues = useCallback(() => {
    setValues(new Set());
  }, []);

  const hasValue = useCallback(
    (value: string) => {
      return values.has(value);
    },
    [values]
  );

  return {
    values,
    addValue,
    removeValue,
    setValue,
    clearValues,
    hasValue,
  };
};
